import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Flame, ArrowRight, Loader2, Mic, ChefHat, ShoppingBag, Trophy, MessageCircle, Bell, Sparkles } from "lucide-react";
import api from "../lib/api";

const FEATURE_ICONS = { voice: Mic, ordering: ChefHat, reservations: ShoppingBag, party: Trophy, chat: MessageCircle, notify: Bell };
const FEATURE_LABELS = {
  voice: "Menu vocale + foto AI",
  ordering: "Ordini al tavolo",
  reservations: "Asporto & Delivery",
  party: "Menuparty Quiz",
  chat: "Chat live · DM tavoli",
  notify: "Push · Promo a tempo",
};

function PlanCard({ plan, primary, loading, onSelect }) {
  const features = plan.features || {};
  const active = Object.entries(features).filter(([, v]) => v).map(([k]) => k);
  return (
    <div data-testid={`choose-plan-${plan.id}`} className={`relative rounded-3xl p-6 sm:p-7 ${primary ? "bg-gradient-to-br from-[#D99B26]/25 via-[#C85A32]/20 to-[#3B503A]/20 border-2 border-[#D99B26] shadow-2xl shadow-[#D99B26]/20" : "bg-white/5 border border-white/10"}`}>
      {primary && <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[#D99B26] text-[#0B0A08] text-[10px] font-black uppercase tracking-widest shadow">Consigliato</div>}
      <div className="text-xs uppercase tracking-widest font-black text-[#D99B26]">{plan.name}</div>
      {plan.tagline && <div className="text-sm text-white/70 mt-1">{plan.tagline}</div>}
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-serif-display text-5xl font-black text-white">{Number(plan.price_eur).toFixed(0)}€</span>
        <span className="text-white/60">/mese</span>
      </div>
      {plan.trial_days > 0 && (
        <div className="mt-1 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#3B503A]/40 text-[#EAF0E9] text-[10px] font-black uppercase tracking-widest">
          <Flame size={10}/> {plan.trial_days} giorni gratis · poi addebito mensile
        </div>
      )}
      <ul className="mt-5 space-y-2 text-sm">
        {active.map((f) => {
          const Icon = FEATURE_ICONS[f] || Sparkles;
          return (
            <li key={f} className="flex gap-2 text-white/95">
              <Icon size={14} className="text-[#D99B26] mt-0.5 flex-shrink-0"/>
              {FEATURE_LABELS[f]}
            </li>
          );
        })}
      </ul>
      {plan.highlights && plan.highlights.length > 0 && (
        <ul className="mt-3 space-y-1.5 text-xs text-white/60 border-t border-white/10 pt-3">
          {plan.highlights.map((h) => (
            <li key={h} className="flex gap-2">
              <Check size={12} className="text-white/40 mt-0.5 flex-shrink-0"/>
              {h}
            </li>
          ))}
        </ul>
      )}
      <button
        data-testid={`choose-plan-cta-${plan.id}`}
        onClick={() => onSelect(plan)}
        disabled={loading}
        className={`mt-6 w-full inline-flex items-center justify-center gap-2 ${primary ? "bg-gradient-to-r from-[#D99B26] to-[#C85A32] hover:brightness-110" : "bg-white/10 hover:bg-white/20"} text-white rounded-full py-3.5 font-black disabled:opacity-60`}
      >
        {loading ? <Loader2 className="animate-spin" size={16}/> : <><Check size={16}/> Scegli {plan.name}</>}
        {!loading && <ArrowRight size={16}/>}
      </button>
    </div>
  );
}

export default function ChoosePlan() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoadingId, setCheckoutLoadingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plans");
        setPlans(data.plans || []);
      } catch {
        toast.error("Errore caricamento piani");
      } finally { setLoading(false); }
    })();
  }, []);

  const startCheckout = async (plan) => {
    setCheckoutLoadingId(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", { plan_id: plan.id, origin_url: window.location.origin });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Sessione di pagamento non creata");
        setCheckoutLoadingId(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore checkout");
      setCheckoutLoadingId(null);
    }
  };

  const menuAi = plans.find((p) => p.id === "plan_menu_ai") || plans[0];
  const allIn = plans.find((p) => p.id === "plan_all_in") || plans[1];

  return (
    <div className="min-h-screen bg-[#0B0A08] text-[#FBF9F5] overflow-hidden relative">
      <div className="fixed -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#C85A32]/25 blur-[120px] pointer-events-none"/>
      <div className="fixed -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#D99B26]/20 blur-[140px] pointer-events-none"/>

      <div className="relative max-w-5xl mx-auto px-5 sm:px-10 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 backdrop-blur border border-white/10 text-[11px] font-bold tracking-widest uppercase text-[#D99B26]">
            <Flame size={12}/> Scegli il tuo piano
          </div>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl font-black">Attiva VOICEMENU</h1>
          <p className="mt-3 text-white/70 max-w-2xl mx-auto">
            Prova gratis per <b className="text-white">3 giorni</b>. Puoi disdire in qualsiasi momento prima della scadenza — nessun addebito.
          </p>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-3xl bg-white/5 border border-white/10 p-6 animate-pulse h-96"/>
            <div className="rounded-3xl bg-white/5 border border-white/10 p-6 animate-pulse h-96"/>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {menuAi && <PlanCard plan={menuAi} primary={false} loading={checkoutLoadingId === menuAi.id} onSelect={startCheckout}/>}
            {allIn && <PlanCard plan={allIn} primary={true} loading={checkoutLoadingId === allIn.id} onSelect={startCheckout}/>}
          </div>
        )}
        <div className="mt-8 text-center">
          <button data-testid="choose-plan-later" onClick={() => navigate("/dashboard")} className="text-sm text-white/50 hover:text-white/80 underline">Salta per ora · vai al dashboard</button>
        </div>
        <div className="mt-8 text-center text-[11px] text-white/40 max-w-2xl mx-auto leading-relaxed">
          Pagamenti gestiti da Stripe. I 3 giorni gratis partono ora. Alla scadenza si attiva l'addebito mensile automatico (60€ per Menu AI, 100€ per All In). IVA inclusa se applicabile.
        </div>
      </div>
    </div>
  );
}
